import { useState, useEffect, useRef } from "react";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import Icon from "./Icon";

function highlight(text, term) {
  if (!text || !term) return text;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "#FEF3C7", borderRadius: 2, padding: "0 1px" }}>
        {text.slice(idx, idx + term.length)}
      </mark>
      {text.slice(idx + term.length)}
    </>
  );
}

const segmentEmoji = (type) => {
  const map = { flight: "✈️", hotel: "🏨", restaurant: "🍽", activity: "⭐", train: "🚆", car: "🚗" };
  return map[type] || "📍";
};

async function indexTrip(tripId) {
  try {
    const segsSnap = await getDocs(collection(db, "trips", tripId, "segments"));
    const text = segsSnap.docs.map(d => {
      const s = d.data();
      return [s.title, s.address, s.detail, s.confirmation, s.phone].filter(Boolean).join(" ");
    }).join(" ").toLowerCase();
    await updateDoc(doc(db, "trips", tripId), { searchText: text });
    return text;
  } catch (e) {
    console.warn("Failed to index trip", tripId, e);
    return null;
  }
}

export default function SearchModal({ user, onClose, onSelectTrip }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [trips, setTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(true);
  const [expandedSegments, setExpandedSegments] = useState({});
  const [loadingSegments, setLoadingSegments] = useState({});
  const [indexingCount, setIndexingCount] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    const load = async () => {
      try {
        const q = query(collection(db, "trips"), where("memberIds", "array-contains", user.uid));
        const snap = await getDocs(q);
        const loadedTrips = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setTrips(loadedTrips);
        setLoadingTrips(false);

        const unindexed = loadedTrips.filter(t => !t.searchText);
        if (unindexed.length > 0) {
          setIndexingCount(unindexed.length);
          for (const trip of unindexed) {
            const text = await indexTrip(trip.id);
            if (text !== null) {
              setTrips(prev => prev.map(t => t.id === trip.id ? { ...t, searchText: text } : t));
            }
            setIndexingCount(prev => prev - 1);
          }
        }
      } catch (err) {
        console.error("Search load error:", err);
        setLoadingTrips(false);
      }
    };
    load();
  }, [user.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const term = searchTerm.toLowerCase().trim();
  const matchingTrips = term
    ? trips.filter(t =>
        t.name?.toLowerCase().includes(term) ||
        t.searchText?.includes(term)
      )
    : [];

  const loadSegments = async (tripId) => {
    if (expandedSegments[tripId] || loadingSegments[tripId]) return;
    setLoadingSegments(prev => ({ ...prev, [tripId]: true }));
    try {
      const snap = await getDocs(collection(db, "trips", tripId, "segments"));
      setExpandedSegments(prev => ({ ...prev, [tripId]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
    } catch (e) {
      console.warn("segment load error", e);
    } finally {
      setLoadingSegments(prev => ({ ...prev, [tripId]: false }));
    }
  };

  const getMatchingSegments = (tripId) => {
    const segs = expandedSegments[tripId];
    if (!segs || !term) return [];
    return segs.filter(seg => {
      const fields = [seg.title, seg.address, seg.detail, seg.confirmation, seg.phone, seg.link]
        .filter(Boolean).join(" ").toLowerCase();
      return fields.includes(term);
    });
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxHeight: "85vh", display: "flex", flexDirection: "column" }}>

        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0 16px" }}>
          <Icon name="search" size={16} style={{ color: "var(--ink-light)", flexShrink: 0 }} />
          <input
            ref={inputRef}
            className="form-input"
            placeholder="Search trips, hotels, flights, confirmations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, border: "none", background: "none", fontSize: 16, padding: "4px 0", outline: "none" }}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-light)", fontSize: 18, lineHeight: 1 }}>
              ×
            </button>
          )}
          <button className="modal-close" onClick={onClose} style={{ flexShrink: 0 }}>
            <Icon name="close" size={14} />
          </button>
        </div>

        {indexingCount > 0 && (
          <div style={{ fontSize: 11, color: "var(--ink-light)", textAlign: "center", marginBottom: 8 }}>
            ⏳ Indexing {indexingCount} trips for search…
          </div>
        )}

        <div style={{ overflowY: "auto", flex: 1 }}>
          {loadingTrips && (
            <div style={{ textAlign: "center", padding: "24px 0", color: "var(--ink-light)", fontSize: 13 }}>
              Loading…
            </div>
          )}

          {!loadingTrips && !term && (
            <div style={{ textAlign: "center", padding: "32px 0", color: "var(--ink-light)", fontSize: 13 }}>
              Search across all your trips, hotels,<br/>flights, confirmations and more.
            </div>
          )}

          {!loadingTrips && term && matchingTrips.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 0", color: "var(--ink-light)", fontSize: 13 }}>
              No results for "{searchTerm}"
            </div>
          )}

          {matchingTrips.map((trip) => {
            const tripNameMatch = trip.name?.toLowerCase().includes(term);
            const hasIndex = !!trip.searchText;
            const segs = getMatchingSegments(trip.id);
            const isExpanded = !!expandedSegments[trip.id];
            const isLoadingSegs = !!loadingSegments[trip.id];

            return (
              <div key={trip.id} style={{ marginBottom: 20 }}>
                <div
                  onClick={() => { onSelectTrip(trip); onClose(); }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "8px 12px", background: "var(--paper-warm)",
                    borderRadius: "var(--radius-sm)", marginBottom: 6, cursor: "pointer",
                    border: tripNameMatch ? "1px solid var(--accent)" : "1px solid var(--border)",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
                      {highlight(trip.name, searchTerm)}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ink-light)" }}>
                      {trip.startDate && trip.endDate ? `${trip.startDate} — ${trip.endDate}` : "Dates TBD"}
                    </div>
                  </div>
                  <Icon name="chevronRight" size={14} style={{ color: "var(--ink-faint)" }} />
                </div>

                {hasIndex && trip.searchText?.includes(term) && !isExpanded && (
                  <button
                    onClick={() => loadSegments(trip.id)}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      fontSize: 11, color: "var(--accent)", padding: "4px 12px",
                      display: "block"
                    }}
                  >
                    {isLoadingSegs ? "Loading matches…" : "▸ Show matching segments"}
                  </button>
                )}

                {segs.map((seg) => (
                  <div
                    key={seg.id}
                    onClick={() => { onSelectTrip(trip); onClose(); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 12px", cursor: "pointer",
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    <div style={{ fontSize: 18, flexShrink: 0 }}>{segmentEmoji(seg.type)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {highlight(seg.title, searchTerm)}
                      </div>
                      {seg.address && (
                        <div style={{ fontSize: 11, color: "var(--ink-light)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {highlight(seg.address, searchTerm)}
                        </div>
                      )}
                      {seg.confirmation && (
                        <div style={{ fontSize: 11, color: "var(--ink-light)" }}>
                          {highlight(seg.confirmation, searchTerm)}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ink-faint)", flexShrink: 0 }}>{seg.date}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
